import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Plus, Sprout, MapPin, Users, FileText, Image } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadImage } from '../../lib/storage';
import { europeanCountries } from '../../lib/constants/countries';
import { Modal } from '../Modal';
import { LocationPicker } from '../LocationPicker';

interface CreateFieldModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateFieldModal({ onClose, onSuccess }: CreateFieldModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    country: '',
    address: '',
    latitude: null as number | null,
    longitude: null as number | null,
    avatar_url: '',
    invitees: [] as { id: string; username: string; avatar_url?: string }[]
  });
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      const avatarUrl = await uploadImage(file, 'community-images');
      if (avatarUrl) {
        setFormData(prev => ({ ...prev, avatar_url: avatarUrl }));
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .ilike('username', `%${query}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  const addInvitee = (user: { id: string; username: string; avatar_url?: string }) => {
    if (!formData.invitees.some(i => i.id === user.id)) {
      setFormData(prev => ({
        ...prev,
        invitees: [...prev.invitees, user]
      }));
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeInvitee = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      invitees: prev.invitees.filter(i => i.id !== userId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.country) {
      setError(t('forms.common.fillRequiredFields'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Create field and get the ID
      const { data: field, error: insertError } = await supabase
        .from('fields')
        .insert([{
          name: formData.name,
          description: formData.description,
          country: formData.country,
          address: formData.address || null,
          latitude: formData.latitude,
          longitude: formData.longitude,
          owner_id: user.id,
          avatar_url: formData.avatar_url || null
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Create invitations for selected users
      if (formData.invitees.length > 0) {
        const { error: inviteError } = await supabase
          .from('field_invitations')
          .insert(
            formData.invitees.map(invitee => ({
              field_id: field.id,
              inviter_id: user.id,
              invitee_id: invitee.id
            }))
          );

        if (inviteError) throw inviteError;
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error creating field:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={t('agriculture.addNewGarden')} onClose={onClose} fullScreenOnMobile={true} wide={true}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Two Column Layout on Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Main Form */}
          <div className="space-y-6">
            {/* Garden Avatar */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-content">
                <Image className="h-5 w-5 text-accent-text" />
                Garden Photo
              </label>
              <div className="flex items-center gap-4">
                <div
                  className="relative w-20 h-20 rounded-full bg-accent-base/10 border-2 border-dashed border-accent-text/30 flex items-center justify-center overflow-hidden cursor-pointer hover:border-accent-text/50 transition-colors"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {formData.avatar_url ? (
                    <img
                      src={formData.avatar_url}
                      alt="Garden"
                      className="w-full h-full object-cover"
                    />
                  ) : uploadingAvatar ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-text"></div>
                  ) : (
                    <Image className="h-8 w-8 text-accent-text/40" />
                  )}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
                <div>
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    className="text-sm text-accent-text hover:text-accent-text/80 font-medium"
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? 'Uploading...' : formData.avatar_url ? 'Change photo' : 'Add photo'}
                  </button>
                  {formData.avatar_url && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, avatar_url: '' }))}
                      className="ml-3 text-sm text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Garden Name */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-content">
                <Sprout className="h-5 w-5 text-accent-text" />
                {t('agriculture.gardenName')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-accent-text/20 rounded-xl focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 transition-all text-base"
                placeholder={t('agriculture.gardenNamePlaceholder')}
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-content">
                <FileText className="h-5 w-5 text-accent-text" />
                {t('agriculture.gardenDescription')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-accent-text/20 rounded-xl focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 transition-all text-base resize-none"
                rows={4}
                placeholder={t('agriculture.gardenDescPlaceholder')}
              />
            </div>

            {/* Country */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-base font-semibold text-content">
                <MapPin className="h-5 w-5 text-accent-text" />
                {t('agriculture.country')}
              </label>
              <select
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-accent-text/20 rounded-xl focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 transition-all text-base bg-white"
                required
              >
                <option value="">{t('agriculture.selectCountry')}</option>
                {europeanCountries.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <LocationPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={(lat, lng, address) => {
                  setFormData(prev => ({
                    ...prev,
                    latitude: lat,
                    longitude: lng,
                    address: address || prev.address
                  }));
                }}
                label={t('agriculture.gardenLocation')}
                helperText={t('agriculture.gardenLocationHelper')}
              />
            </div>
          </div>

          {/* Right Column - Invite Members */}
          <div className="space-y-6">
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-base font-semibold text-content">
                <Users className="h-5 w-5 text-accent-text" />
                {t('agriculture.inviteTeam')}
              </label>
              <p className="text-sm text-content/60">
                {t('agriculture.inviteTeamDesc')}
              </p>
              
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-content/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="w-full pl-12 pr-4 py-3 border-2 border-accent-text/20 rounded-xl focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 transition-all text-base"
                  placeholder={t('agriculture.searchUsername')}
                />
              </div>

              {searchResults.length > 0 && (
                <div className="border-2 border-accent-text/10 rounded-xl divide-y divide-accent-text/10 bg-white shadow-sm overflow-hidden">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 flex items-center justify-between hover:bg-accent-base/5 transition-colors cursor-pointer"
                      onClick={() => addInvitee(user)}
                    >
                      <div className="flex items-center gap-3">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.username}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-accent-base/20 flex items-center justify-center">
                            <span className="text-base font-semibold text-accent-text">
                              {user.username[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-content text-base">
                          {user.username}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addInvitee(user);
                        }}
                        className="p-2 text-accent-text hover:bg-accent-base/20 rounded-full transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {formData.invitees.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-content mb-3">{t('agriculture.selectedMembers')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.invitees.map((invitee) => (
                      <div
                        key={invitee.id}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-base/10 border border-accent-text/20"
                      >
                        {invitee.avatar_url ? (
                          <img
                            src={invitee.avatar_url}
                            alt={invitee.username}
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-accent-base/20 flex items-center justify-center">
                            <span className="text-xs font-medium text-accent-text">
                              {invitee.username[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium text-accent-text">{invitee.username}</span>
                        <button
                          type="button"
                          onClick={() => removeInvitee(invitee.id)}
                          className="ml-1 p-0.5 text-accent-text hover:bg-accent-base/20 rounded-full transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-accent-text/10">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-base font-medium text-content hover:text-content/80 rounded-xl hover:bg-accent-base/10 transition-colors border-2 border-transparent hover:border-accent-text/20"
          >
            {t('forms.common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 text-base font-semibold text-white bg-accent-text rounded-xl hover:bg-accent-text/90 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('forms.common.creating')}
              </>
            ) : (
              <>
                <Sprout className="h-5 w-5" />
                {t('agriculture.addGarden')}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}